const ALLOWED_TAGS = new Set([
  "h1", "h2", "h3", "p", "br", "strong", "b", "em", "i", "u",
  "ul", "ol", "li", "span", "a", "div", "font",
]);

export const sanitizeNewsHtml = (html) => {
  if (!html || typeof html !== "string") {
    return "";
  }

  let out = html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, "")
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/(href|src)\s*=\s*(['"])\s*javascript:[\s\S]*?\2/gi, "$1=$2#$2");

  out = out.replace(/<\/?([a-z0-9]+)([^>]*)>/gi, (match, tag, attrs) => {
    const t = tag.toLowerCase();
    if (!ALLOWED_TAGS.has(t)) {
      return "";
    }
    if (t === "br") {
      return "<br />";
    }
    if (match.startsWith("</")) {
      return `</${t}>`;
    }

    let safeAttrs = "";
    if (t === "a") {
      const href = attrs.match(/href\s*=\s*(['"])(.*?)\1/i);
      if (href && /^(https?:|mailto:|#)/i.test(href[2])) {
        safeAttrs += ` href="${href[2].replace(/"/g, "")}" target="_blank" rel="noopener noreferrer"`;
      }
    }
    if (t === "font") {
      const color = attrs.match(/color\s*=\s*(['"])(.*?)\1/i);
      if (color) {
        safeAttrs += ` color="${color[2].replace(/"/g, "")}"`;
      }
    }
    const style = attrs.match(/style\s*=\s*(['"])(.*?)\1/i);
    if (style) {
      const filtered = style[2]
        .split(";")
        .map((part) => part.trim())
        .filter((part) =>
          /^(color|background-color|font-size|font-weight|font-style|text-decoration)\s*:/i.test(part)
        )
        .join("; ");
      if (filtered) {
        safeAttrs += ` style="${filtered}"`;
      }
    }
    return `<${t}${safeAttrs}>`;
  });

  return out.slice(0, 20000);
};

export const newsBodyHasText = (html) =>
  Boolean(sanitizeNewsHtml(html).replace(/<[^>]+>/g, "").trim());

export const splitNewsLeadAndBody = (html) => {
  const source = html || "";
  if (!source.trim()) {
    return { leadHtml: "", bodyHtml: "" };
  }
  const leadMatch = source.match(/^(?:\s*<p\b[\s\S]*?<\/p>)+/i);
  if (!leadMatch) {
    return { leadHtml: "", bodyHtml: source };
  }
  const rest = source.slice(leadMatch[0].length).trim();
  if (!rest) {
    return { leadHtml: "", bodyHtml: source };
  }
  return { leadHtml: leadMatch[0].trim(), bodyHtml: rest };
};

const BLOCK_RE = /<(p|h1|h2|h3|ul|ol|div)(\s[^>]*)?>[\s\S]*?<\/\1>/gi;
const LIST_ITEM_RE = /<li[\s\S]*?<\/li>/gi;
const MAX_LIST_ITEMS_PER_PAGE = 4;
const MAX_TEXT_PER_PAGE = 280;

const splitListBlock = (block) => {
  const match = block.match(/^<(ul|ol)([^>]*)>([\s\S]*)<\/\1>$/i);
  if (!match) {
    return [block];
  }
  const tag = match[1];
  const items = match[3].match(LIST_ITEM_RE) || [];
  if (items.length <= MAX_LIST_ITEMS_PER_PAGE) {
    return [block];
  }
  const pages = [];
  for (let index = 0; index < items.length; index += MAX_LIST_ITEMS_PER_PAGE) {
    pages.push(`<${tag}>${items.slice(index, index + MAX_LIST_ITEMS_PER_PAGE).join("")}</${tag}>`);
  }
  return pages;
};

export const splitNewsHtmlIntoPages = (html) => {
  const sanitized = sanitizeNewsHtml(html);
  if (!sanitized.trim()) {
    return [""];
  }

  const blocks = sanitized.match(BLOCK_RE);
  if (!blocks) {
    const text = sanitized.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (text.length <= MAX_TEXT_PER_PAGE) {
      return [sanitized];
    }
    const pages = [];
    let remaining = text;
    while (remaining.length) {
      if (remaining.length <= MAX_TEXT_PER_PAGE) {
        pages.push(`<p>${remaining}</p>`);
        break;
      }
      let cut = remaining.lastIndexOf(" ", MAX_TEXT_PER_PAGE);
      if (cut < MAX_TEXT_PER_PAGE / 2) {
        cut = MAX_TEXT_PER_PAGE;
      }
      pages.push(`<p>${remaining.slice(0, cut).trim()}</p>`);
      remaining = remaining.slice(cut).trim();
    }
    return pages;
  }
  const units = blocks.flatMap(splitListBlock);
  const pages = [];
  let current = [];
  let currentLength = 0;

  const flush = () => {
    if (current.length) {
      pages.push(current.join(""));
      current = [];
      currentLength = 0;
    }
  };

  units.forEach((unit) => {
    const textLength = unit.replace(/<[^>]+>/g, "").length;
    if (current.length && currentLength + textLength > MAX_TEXT_PER_PAGE) {
      flush();
    }
    current.push(unit);
    currentLength += textLength;
  });
  flush();

  return pages.length ? pages : [sanitized];
};

export const buildFeatureNewsSlides = (items) =>
  (Array.isArray(items) ? items : []).flatMap((item) => {
    const pages = splitNewsHtmlIntoPages(item.bodyHtml || item.body || "");
    return pages.map((pageHtml, pageIndex) => ({
      ...item,
      slideId: `${item.id}:${pageIndex}`,
      pageHtml,
      pageIndex,
      pageCount: pages.length,
    }));
  });

